import axios from "axios";
import { TermsAndConditions, PrivacyPolicy, User } from "../types";
import AuthService from "./AuthService";
import { legalService } from "./ServiceFactory";

// Use Wiremock server in development, real API in production
const API_URL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_API_URL || "http://localhost:8080"
    : process.env.REACT_APP_API_URL || "https://api.example.com";

const IS_LOCAL_DEV = process.env.NODE_ENV === "development";
const S3_BUCKET_URL = IS_LOCAL_DEV
  ? "http://localhost:4566/legal-docs"
  : process.env.REACT_APP_S3_BUCKET_URL || "https://s3.example.com/legal-docs";

console.log(`Using Legal API URL: ${API_URL}`);
console.log(`Using S3 Bucket URL: ${S3_BUCKET_URL}`);

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_URL,
});

// Add interceptor for authentication token
apiClient.interceptors.request.use(async (config) => {
  const token = await AuthService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const LegalService = {
  // Get the latest terms and conditions
  getLatestTerms: async (): Promise<TermsAndConditions> => {
    try {
      const response = await apiClient.get("/legal/terms/latest");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch latest terms:", error);
      // Fallback to getting from S3 directly if API fails
      return fetchDocumentFromS3("terms-and-conditions.json");
    }
  },

  // Get the latest privacy policy
  getLatestPrivacy: async (): Promise<PrivacyPolicy> => {
    try {
      const response = await apiClient.get("/legal/privacy/latest");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch latest privacy policy:", error);
      // Fallback to getting from S3 directly if API fails
      return fetchDocumentFromS3("privacy-policy.json");
    }
  },

  // Accept terms and conditions
  acceptTerms: async (userId: string, version: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/acceptance/terms`, { version });
  },

  // Accept privacy policy
  acceptPrivacy: async (userId: string, version: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/acceptance/privacy`, { version });
  },

  // Check if the user has accepted the latest terms and privacy policy
  checkUserAcceptance: async (
    user: User
  ): Promise<{ termsAccepted: boolean; privacyAccepted: boolean }> => {
    try {
      const [latestTerms, latestPrivacy] = await Promise.all([
        LegalService.getLatestTerms(),
        LegalService.getLatestPrivacy(),
      ]);

      return {
        termsAccepted:
          user.acceptedTerms && user.termsVersion === latestTerms.version,
        privacyAccepted:
          user.acceptedPrivacy && user.privacyVersion === latestPrivacy.version,
      };
    } catch (error) {
      console.error("Failed to check user acceptance:", error);
      return { termsAccepted: false, privacyAccepted: false };
    }
  },
};

// Helper function to fetch document directly from S3 bucket
async function fetchDocumentFromS3<T>(filename: string): Promise<T> {
  try {
    // For LocalStack, we need to make sure we're accessing the files correctly
    // LocalStack uses the URL pattern: http://localhost:4566/bucket-name/file-name
    // While production S3 uses: https://bucket-name.s3.region.amazonaws.com/file-name
    // or https://s3-url/bucket-name/file-name
    const url = IS_LOCAL_DEV
      ? `${S3_BUCKET_URL}/${filename}`
      : `${S3_BUCKET_URL}/${filename}`;

    console.log(`Fetching legal document from: ${url}`);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch ${filename} from S3:`, error);
    throw new Error(`Failed to fetch legal document: ${filename}`);
  }
}

export default legalService;
